import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

import { compare } from '../../validators/compare.validator';
import { PassphraseFormChanges } from '../../models/passphrase-form-changes.model';
import { PassphraseSummary } from '../../models/passphrase-summary.model';

const EMPTY = '';

@Component({
  selector: 'app-choose-passphrase',
  templateUrl: './choose-passphrase.component.html',
  styleUrls: ['./choose-passphrase.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChoosePassphraseComponent implements OnInit, OnDestroy {
  @Output() formInitialization: EventEmitter<PassphraseFormChanges> =
    new EventEmitter();
  @Output() formChanges: EventEmitter<PassphraseFormChanges> =
    new EventEmitter();

  passphraseForm: FormGroup = this.formBuilder.group({
    preference: ['default', Validators.required],
    custom: this.formBuilder.group(
      {
        passphrase: [EMPTY],
        repeat: [EMPTY],
      },
      {
        validators: [compare(['passphrase', 'repeat'])],
      }
    ),
  });

  private subscriptions: Subscription[] = [];

  private readonly PASSPHRASE_VALIDATORS: ValidatorFn[] = [
    Validators.required,
    Validators.minLength(6),
    Validators.maxLength(20),
    Validators.pattern(/^[a-zA-Z0-9]{6,20}$/),
  ];
  private readonly REPEAT_PASSPHRASE_VALIDATORS: ValidatorFn[] = [
    Validators.required,
  ];

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    const preferenceSubscription = this.preference.valueChanges
      .pipe(tap(() => this.onPreferenceChange()))
      .subscribe();
    this.subscriptions.push(preferenceSubscription);

    const formValidSubscription = this.passphraseForm.valueChanges
      .pipe(tap(() => this.emitFormChangesEvent()))
      .subscribe();
    this.subscriptions.push(formValidSubscription);

    this.formInitialization.emit(this.prepareEventsPayload());
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  get isValid(): boolean {
    return this.passphraseForm.valid;
  }

  get preference(): FormControl {
    return this.passphraseForm.get('preference') as FormControl;
  }

  get passphraseGroup(): FormGroup {
    return this.passphraseForm.get('custom') as FormGroup;
  }

  get passphrase(): FormControl {
    return this.passphraseForm.get('custom.passphrase') as FormControl;
  }

  get repeatPassphrase(): FormControl {
    return this.passphraseForm.get('custom.repeat') as FormControl;
  }

  get useDefaultValue(): boolean {
    return this.preference.value === 'default';
  }

  resetForm(): void {
    this.preference.setValue('default');
  }

  onPreferenceChange(): void {
    this.resetControls([this.passphrase, this.repeatPassphrase]);

    if (this.useDefaultValue) {
      this.passphrase.setValidators([]);
      this.repeatPassphrase.setValidators([]);
    } else {
      this.passphrase.setValidators(this.PASSPHRASE_VALIDATORS);
      this.repeatPassphrase.setValidators(this.REPEAT_PASSPHRASE_VALIDATORS);
    }

    this.passphrase.updateValueAndValidity();
    this.repeatPassphrase.updateValueAndValidity();
  }

  passphraseValidator(): Function {
    return () => this.passphrase.valid;
  }

  repeatPassphraseValidator(): Function {
    return () => this.repeatPassphrase.valid && this.passphraseGroup.valid;
  }

  getCurrentPassphraseErrorMessage(): string {
    return this.getErrorMessage(
      this.passphrase,
      'CHOOSE_PASSPHRASE_PASSPHRASE'
    );
  }

  getRepeatCurrentPassphraseErrorMessage(): string {
    const prefix = 'CHOOSE_PASSPHRASE_CONFIRM_PASSPHRASE';
    return (
      this.getErrorMessage(this.repeatPassphrase, prefix) ||
      this.getErrorMessage(this.passphraseGroup, prefix)
    );
  }

  private getErrorMessage(
    control: FormControl | FormGroup,
    prefix: string
  ): string {
    const { invalid, touched, errors } = control;
    const hasError = invalid && touched && !!errors;
    if (hasError) {
      const error = Object.keys(errors)[0];
      return this.translateService.instant(`${prefix}_${error.toUpperCase()}`);
    } else {
      return EMPTY;
    }
  }

  private resetControls(controls: FormControl[]): void {
    controls.forEach((control) => this.resetControl(control));
  }

  private resetControl(control: FormControl): void {
    control.setValue(EMPTY);
    control.markAsUntouched();
    control.markAsPristine();
  }

  private emitFormChangesEvent(): void {
    this.formChanges.emit(this.prepareEventsPayload());
  }

  private prepareEventsPayload(): PassphraseFormChanges {
    const summary: PassphraseSummary = this.useDefaultValue
      ? { type: this.preference.value }
      : { type: this.preference.value, passphrase: this.passphrase.value };
    return {
      form: this.passphraseForm,
      summary,
    };
  }
}
